import type { IconsNames } from '@eqi-investimentos/icons/web';
import type { FC } from 'react';
import { useRouter } from 'next/navigation';
import useTranslation from 'next-translate/useTranslation';
import { Flex, Switch, Text, type FlexProps } from '@eqi-investimentos/react';
import { Icon } from '@/components/atoms/Icon';
import { IfRender } from '@/utils/jsx';
export const ProfileItem: FC<ProfileItemProps> = ({
  ID = '',
  leftIcon,
  title,
  type,
  disabled = false,
  isExternal,
  path,
  showArrow = true,
  onClick,
  onChange,
  value,
  ...props
}) => {
  const {
    t
  } = useTranslation('common');
  const {
    push
  } = useRouter();
  const color = colorStyle[String(disabled) as keyof typeof colorStyle];
  const handleClick = () => {
    if (type === 'switch' && !!onChange) return onChange();
    if (type === 'action') return onClick();
    if (isExternal) return window.open(path, '_blank');
    return push(path);
  };
  return <Flex id={ID} onClickCapture={handleClick} {...defaultStyle(disabled)} {...props} data-sentry-element="Flex" data-sentry-component="ProfileItem" data-sentry-source-file="Item.tsx">
      <Text size="xl" color="content.primary" data-sentry-element="Text" data-sentry-source-file="Item.tsx">
        {title}
      </Text>
      <IfRender condition={type === 'switch'} data-sentry-element="IfRender" data-sentry-source-file="Item.tsx">
        <Switch isDisabled={disabled} isChecked={value} zIndex={-1} data-sentry-element="Switch" data-sentry-source-file="Item.tsx" />
      </IfRender>
      <IfRender condition={type !== 'switch'} data-sentry-element="IfRender" data-sentry-source-file="Item.tsx">
        <IfRender condition={showArrow && !disabled} data-sentry-element="IfRender" data-sentry-source-file="Item.tsx">
          <Icon iconName="ChevronRight" color={color} w={24} h={24} data-sentry-element="Icon" data-sentry-source-file="Item.tsx" />
        </IfRender>
        <IfRender condition={disabled} data-sentry-element="IfRender" data-sentry-source-file="Item.tsx">
          <Text fontSize="xs" lineHeight="0.875rem" color="content.secondary" data-sentry-element="Text" data-sentry-source-file="Item.tsx">
            {t('attributes.soon')}
          </Text>
        </IfRender>
      </IfRender>
    </Flex>;
};
const defaultStyle = (disabled: boolean): FlexProps => ({
  justifyContent: 'space-between',
  alignItems: 'center',
  py: 5,
  w: 'full',
  borderBottom: '1px solid var(--color-eqi-gray-lighten-3)',
  cursor: 'pointer',
  pointerEvents: disabled ? 'none' : 'auto',
  _hover: {
    '& > div p, svg': {
      color: disabled ? 'content.secondary' : 'content.brand'
    }
  },
  sx: {
    _last: {
      borderBottomWidth: 0
    }
  }
});
const colorStyle = {
  true: 'content.secondary',
  false: 'content.primary'
};
type ProfileItemProps = Omit<FlexProps, 'children' | 'onClick' | 'onChange' | 'title'> & {
  ID?: string;
  leftIcon: IconsNames;
  title: string;
  disabled?: boolean;
  showArrow?: boolean;
} & ({
  type: 'switch';
  onChange: () => void;
  value: boolean;
  isExternal?: never;
  path?: never;
  onClick?: never;
} | {
  type: 'navigation';
  isExternal: boolean;
  path: string;
  onClick?: never;
  value?: never;
  onChange?: never;
} | {
  type: 'action';
  onClick: () => void;
  isExternal?: never;
  path?: never;
  onChange?: never;
  value?: never;
});